import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CourseContent = ({ course, activeTab, onTabChange }) => {
  const [expandedModules, setExpandedModules] = useState({});
  const [loadingProgress, setLoadingProgress] = useState({});

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const handleLessonProgress = async (lessonId) => {
    setLoadingProgress((prev) => ({ ...prev, [lessonId]: true }));
    try {
      const token = localStorage.getItem("access");
      await axios.post(
        `${BASE_URL}/courses/lessons/${lessonId}/progress/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      //console.error("Failed to update lesson progress:", err);
    } finally {
      setLoadingProgress((prev) => ({ ...prev, [lessonId]: false }));
    }
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case "video":
        return "fas fa-play-circle";
      case "document":
        return "fas fa-file-alt";
      case "quiz":
        return "fas fa-question-circle";
      default:
        return "fas fa-file";
    }
  };

  // Extract learning outcomes and materials from description or assume static if not provided
  const learningOutcomes = course.description
    ? course.description.split("\n").filter((line) => line.trim().startsWith("-"))
    : [];
  const materials = course.description
    ? course.description.split("\n").filter((line) => line.trim().startsWith("*"))
    : [];

  // Format date for announcements
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="course-content">
      {/* Tab Navigation */}
      <div className="course-tabs">
        <button
          className={`course-tab ${activeTab === "course-info" ? "active" : ""}`}
          onClick={() => onTabChange("course-info")}
        >
          Course Info
        </button>
        <button
          className={`course-tab ${activeTab === "announcements" ? "active" : ""}`}
          onClick={() => onTabChange("announcements")}
        >
          Announcements
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "course-info" && (
        <div className="course-info-content">
          {/* What Will You Learn */}
          {learningOutcomes.length > 0 && (
            <div className="learning-outcomes mb-5">
              <h3 className="section-title">What Will You Learn?</h3>
              <div className="row">
                {learningOutcomes.map((outcome, index) => (
                  <div key={index} className="col-md-6 mb-3">
                    <div className="learning-outcome">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      {outcome.replace(/^-/, "").trim()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Course Content */}
          <div className="course-curriculum">
            <h3 className="section-title">Course Content</h3>
            <div className="modules-list">
              {course.modules.map((module) => (
                <div key={module.id} className="module-item">
                  <div
                    className="module-header"
                    onClick={() => module.lessons.length > 0 && toggleModule(module.id)}
                    style={{ cursor: module.lessons.length > 0 ? "pointer" : "default" }}
                  >
                    <div className="d-flex align-items-center">
                      <span className="module-title">{module.title}</span>
                    </div>
                    {module.lessons.length > 0 && (
                      <i
                        className={`fas ${
                          expandedModules[module.id] ? "fa-chevron-up" : "fa-chevron-down"
                        }`}
                      ></i>
                    )}
                  </div>

                  {expandedModules[module.id] && module.lessons.length > 0 && (
                    <div className="module-lessons">
                      {module.lessons.map((lesson) => (
                        <div key={lesson.id} className="lesson-item">
                          <Link
                              to={`/course/${course.id}/learn/${lesson.id}`}
                              style={{ textDecoration: "none", color: "inherit" }}
                            >
                          <div className="lesson-content">
                            <div className="d-flex align-items-center">
                              <i className={`${getLessonIcon(lesson.content_type)} lesson-icon me-3`}></i>
                              <span className="lesson-title">{lesson.title}</span>
                            </div>
                            <div className="lesson-meta">
                              {lesson.duration && (
                                <span className="lesson-duration">{lesson.duration}</span>
                              )}
                              <i
                                className={`fas fa-check-circle ${
                                  lesson.completed ? "text-success" : "text-muted"
                                } ms-2`}
                                onClick={() => !lesson.completed && handleLessonProgress(lesson.id)}
                                style={{ cursor: lesson.completed ? "default" : "pointer" }}
                              ></i>
                            </div>
                          </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "announcements" && (
        <div className="announcements-content">
          {Array.isArray(course.announcements) && course.announcements.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-bullhorn fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No announcements yet</h5>
              <p className="text-muted">Check back later for course updates and announcements.</p>
            </div>
          ) : (
            <div className="announcements-list py-4">
              {Array.isArray(course.announcements) ? (
                course.announcements.map((announcement) => (
                  <div key={announcement.id} className="announcement-item mb-4 p-3 border rounded">
                    <h5 className="announcement-title">{announcement.title}</h5>
                    <div
                      className="announcement-content mb-2"
                      dangerouslySetInnerHTML={{ __html: announcement.content }}
                    />
                    <small className="text-muted">
                      Posted on {formatDate(announcement.created_at)}
                    </small>
                  </div>
                ))
              ) : (
                <div className="text-center py-5 text-danger">
                  Invalid announcement data received.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CourseContent;